import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Eye,
  Filter,
  Search,
  Calendar,
  MapPin,
  Camera,
  User,
  RefreshCw,
  UserCheck,
  Settings,
  MessageSquare,
  Star,
  Download,
  BarChart3
} from 'lucide-react';
import toast from 'react-hot-toast';
import './ReportManagement.css';
import apiClient from '../../services/api';

const ReportManagement = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const statusOptions = [
    { value: 'all', label: 'All Reports', icon: <FileText size={16} />, color: '#64748b' },
    { value: 'pending', label: 'Pending', icon: <Clock size={16} />, color: '#f59e0b' },
    { value: 'acknowledged', label: 'Acknowledged', icon: <Eye size={16} />, color: '#3b82f6' },
    { value: 'in_progress', label: 'In Progress', icon: <RefreshCw size={16} />, color: '#8b5cf6' },
    { value: 'resolved', label: 'Resolved', icon: <CheckCircle size={16} />, color: '#10b981' },
    { value: 'rejected', label: 'Rejected', icon: <AlertCircle size={16} />, color: '#ef4444' }
  ];

  const issueTypeLabels = {
    overflow: 'Bin Overflow',
    damaged: 'Damaged Bin',
    missing: 'Missing Bin',
    blocked_access: 'Blocked Access',
    foul_smell: 'Foul Smell',
    pest_infestation: 'Pest Infestation',
    improper_waste: 'Improper Waste',
    maintenance_needed: 'Maintenance Needed',
    other: 'Other Issue'
  };

  const priorityColors = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444',
    urgent: '#8b5cf6'
  };

  useEffect(() => {
    fetchReports();
    fetchWorkers();
    fetchAnalytics();
  }, [filter, currentPage]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      
      if (filter !== 'all') {
        params.append('status', filter);
      }

      const response = await apiClient.get(`/reports?${params}`);
      setReports(response.data.reports);
      setTotalReports(response.data.pagination.totalReports);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to load reports');
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      const response = await apiClient.get('/workers');
      setWorkers(response.data.workers || response.data);
    } catch (err) {
      console.error('Error fetching workers:', err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await apiClient.get('/reports/analytics/summary');
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  };

  const updateReportStatus = async (reportId, status, adminNotes = '') => {
    try {
      await apiClient.patch(`/reports/${reportId}/status`, {
        status,
        adminNotes
      });
      
      toast.success('Report status updated successfully');
      fetchReports();
    } catch (err) {
      console.error('Error updating report status:', err);
      toast.error('Failed to update report status');
    }
  };

  const assignReportToWorker = async (reportId, workerId, taskDetails) => {
    try {
      await apiClient.post(`/reports/${reportId}/assign`, {
        workerId,
        taskTitle: taskDetails.title,
        taskDescription: taskDetails.description,
        scheduledStartTime: taskDetails.startTime,
        scheduledEndTime: taskDetails.endTime
      });
      
      toast.success('Report assigned to worker successfully');
      setShowAssignModal(false);
      setSelectedReport(null);
      fetchReports();
    } catch (err) {
      console.error('Error assigning report:', err);
      toast.error('Failed to assign report to worker');
    }
  };

  const getStatusIcon = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.icon : <FileText size={16} />;
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : '#64748b';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Less than an hour ago';
    }
  };

  const filteredReports = reports.filter(report =>
    report.bin?.binId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    issueTypeLabels[report.issueType]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.reportedBy?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && reports.length === 0) {
    return (
      <div className="report-management-container">
        <div className="loading-section">
          <div className="loader"></div>
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="report-management-container">
      {/* Header */}
      <div className="management-header">
        <div className="header-content">
          <h1>Report Management</h1>
          <p>Monitor and manage user-submitted bin reports</p>
        </div>
        <div className="header-actions">
          <button onClick={fetchReports} className="refresh-btn">
            <RefreshCw size={16} />
            Refresh
          </button>
          <button className="export-btn">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="analytics-cards">
          <div className="analytics-card">
            <div className="card-icon total">
              <FileText size={20} />
            </div>
            <div className="card-content">
              <h3>{analytics.totalReports}</h3>
              <p>Total Reports</p>
            </div>
          </div>
          <div className="analytics-card">
            <div className="card-icon pending">
              <Clock size={20} />
            </div>
            <div className="card-content">
              <h3>{analytics.summary?.pending?.count || 0}</h3>
              <p>Pending</p>
            </div>
          </div>
          <div className="analytics-card">
            <div className="card-icon resolved">
              <CheckCircle size={20} />
            </div>
            <div className="card-content">
              <h3>{analytics.summary?.resolved?.count || 0}</h3>
              <p>Resolved</p>
            </div>
          </div>
          <div className="analytics-card">
            <div className="card-icon overdue">
              <AlertCircle size={20} />
            </div>
            <div className="card-content">
              <h3>{analytics.overdueCount || 0}</h3>
              <p>Overdue</p>
            </div>
          </div>
          <div className="analytics-card">
            <div className="card-icon time">
              <BarChart3 size={20} />
            </div>
            <div className="card-content">
              <h3>{Math.round(analytics.averageResolutionTime || 0)}m</h3>
              <p>Avg Resolution</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="status-filters">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setFilter(option.value);
                setCurrentPage(1);
              }}
              className={`filter-btn ${filter === option.value ? 'active' : ''}`}
              style={{ '--filter-color': option.color }}
            >
              {option.icon}
              {option.label}
            </button>
          ))}
        </div>
        
        <div className="search-section">
          <div className="search-input-wrapper">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Search by bin ID, description, issue type, or reporter..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {/* Reports Table */}
      {error ? (
        <div className="error-section">
          <AlertCircle size={48} className="error-icon" />
          <h2>Unable to Load Reports</h2>
          <p>{error}</p>
          <button onClick={fetchReports} className="retry-btn">
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="empty-section">
          <FileText size={48} className="empty-icon" />
          <h2>No Reports Found</h2>
          <p>
            {searchTerm 
              ? `No reports match your search "${searchTerm}"`
              : filter === 'all' 
                ? "No reports have been submitted yet"
                : `No ${filter} reports found`
            }
          </p>
        </div>
      ) : (
        <div className="reports-table-container">
          <div className="reports-table">
            <div className="table-header">
              <div className="header-cell">Report ID</div>
              <div className="header-cell">Issue</div>
              <div className="header-cell">Bin</div>
              <div className="header-cell">Reporter</div>
              <div className="header-cell">Priority</div>
              <div className="header-cell">Status</div>
              <div className="header-cell">Created</div>
              <div className="header-cell">Actions</div>
            </div>
            
            {filteredReports.map((report) => (
              <div key={report._id} className="table-row">
                <div className="table-cell">
                  <span className="report-id">#{report.reportId}</span>
                </div>
                <div className="table-cell">
                  <div className="issue-info">
                    <span className="issue-type">
                      {issueTypeLabels[report.issueType] || report.issueType}
                    </span>
                    <span className="issue-description">
                      {report.description.length > 50 
                        ? `${report.description.substring(0, 50)}...`
                        : report.description
                      }
                    </span>
                  </div>
                </div>
                <div className="table-cell">
                  <div className="bin-info">
                    <span className="bin-id">{report.bin?.binId || report.binId}</span>
                    {report.bin?.address?.streetAddress && (
                      <span className="bin-location">
                        <MapPin size={12} />
                        {report.bin.address.streetAddress.substring(0, 30)}...
                      </span>
                    )}
                  </div>
                </div>
                <div className="table-cell">
                  <div className="reporter-info">
                    <User size={14} />
                    <span>{report.reportedBy?.name || 'Unknown'}</span>
                  </div>
                </div>
                <div className="table-cell">
                  <div 
                    className="priority-badge"
                    style={{ backgroundColor: priorityColors[report.priority] }}
                  >
                    {report.priority}
                  </div>
                </div>
                <div className="table-cell">
                  <div 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(report.status) }}
                  >
                    {getStatusIcon(report.status)}
                    {report.status.replace('_', ' ')}
                  </div>
                </div>
                <div className="table-cell">
                  <span className="date-info">{formatDate(report.createdAt)}</span>
                </div>
                <div className="table-cell">
                  <div className="action-buttons">
                    <button 
                      className="action-btn view"
                      onClick={() => setSelectedReport(report)}
                      title="View Details"
                    >
                      <Eye size={14} />
                    </button>
                    {report.status === 'pending' && (
                      <button 
                        className="action-btn acknowledge"
                        onClick={() => updateReportStatus(report._id, 'acknowledged')}
                        title="Acknowledge"
                      >
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {['pending', 'acknowledged'].includes(report.status) && (
                      <button 
                        className="action-btn assign"
                        onClick={() => {
                          setSelectedReport(report);
                          setShowAssignModal(true);
                        }}
                        title="Assign to Worker"
                      >
                        <UserCheck size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({totalReports} total reports)
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Report Details Modal */}
      {selectedReport && !showAssignModal && (
        <ReportDetailsModal 
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          onUpdateStatus={updateReportStatus}
          onAssign={() => setShowAssignModal(true)}
        />
      )}

      {/* Assign Worker Modal */}
      {showAssignModal && selectedReport && (
        <AssignWorkerModal
          report={selectedReport}
          workers={workers}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedReport(null);
          }}
          onAssign={assignReportToWorker}
        />
      )}
    </div>
  );
};

// Report Details Modal Component
const ReportDetailsModal = ({ report, onClose, onUpdateStatus, onAssign }) => {
  const [adminNotes, setAdminNotes] = useState(report.adminNotes || '');

  const issueTypeLabels = {
    overflow: 'Bin Overflow',
    damaged: 'Damaged Bin',
    missing: 'Missing Bin',
    blocked_access: 'Blocked Access',
    foul_smell: 'Foul Smell',
    pest_infestation: 'Pest Infestation',
    improper_waste: 'Improper Waste',
    maintenance_needed: 'Maintenance Needed',
    other: 'Other Issue'
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Report Details</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-body">
          <div className="report-details-grid">
            <div className="detail-section">
              <h3>Report Information</h3>
              <div className="detail-item">
                <span className="label">Report ID:</span>
                <span className="value">#{report.reportId}</span>
              </div>
              <div className="detail-item">
                <span className="label">Issue Type:</span>
                <span className="value">{issueTypeLabels[report.issueType]}</span>
              </div>
              <div className="detail-item">
                <span className="label">Priority:</span>
                <span className="value priority" style={{ color: report.priority === 'urgent' ? '#8b5cf6' : report.priority === 'high' ? '#ef4444' : report.priority === 'medium' ? '#f59e0b' : '#10b981' }}>
                  {report.priority}
                </span>
              </div>
              <div className="detail-item">
                <span className="label">Status:</span>
                <span className="value">{report.status.replace('_', ' ')}</span>
              </div>
            </div>

            <div className="detail-section">
              <h3>Bin Information</h3>
              <div className="detail-item">
                <span className="label">Bin ID:</span>
                <span className="value">{report.bin?.binId || report.binId}</span>
              </div>
              <div className="detail-item">
                <span className="label">Location:</span>
                <span className="value">{report.bin?.address?.streetAddress || 'Not specified'}</span>
              </div>
              <div className="detail-item">
                <span className="label">Distance from Reporter:</span>
                <span className="value">{report.distanceFromBin || 'N/A'}m</span>
              </div>
            </div>

            <div className="detail-section">
              <h3>Reporter Information</h3>
              <div className="detail-item">
                <span className="label">Name:</span>
                <span className="value">{report.reportedBy?.name || 'Unknown'}</span>
              </div>
              <div className="detail-item">
                <span className="label">Email:</span>
                <span className="value">{report.reportedBy?.email || 'Not provided'}</span>
              </div>
              <div className="detail-item">
                <span className="label">Reported:</span>
                <span className="value">{new Date(report.createdAt).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="description-section">
            <h3>Description</h3>
            <p>{report.description}</p>
          </div>

          {report.photos && report.photos.length > 0 && (
            <div className="photos-section">
              <h3>Photos ({report.photos.length})</h3>
              <div className="photos-grid">
                {report.photos.map((photo, index) => (
                  <img 
                    key={index} 
                    src={photo} 
                    alt={`Report photo ${index + 1}`}
                    className="report-photo"
                  />
                ))}
              </div>
            </div>
          )}

          <div className="notes-section">
            <h3>Admin Notes</h3>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes about this report..."
              className="notes-textarea"
              rows={3}
            />
          </div>
        </div>

        <div className="modal-footer">
          <div className="status-actions">
            {report.status === 'pending' && (
              <button 
                onClick={() => onUpdateStatus(report._id, 'acknowledged', adminNotes)}
                className="action-btn acknowledge"
              >
                <Eye size={16} />
                Acknowledge
              </button>
            )}
            {['pending', 'acknowledged'].includes(report.status) && (
              <button 
                onClick={onAssign}
                className="action-btn assign"
              >
                <UserCheck size={16} />
                Assign to Worker
              </button>
            )}
            {report.status !== 'resolved' && report.status !== 'rejected' && (
              <button 
                onClick={() => onUpdateStatus(report._id, 'resolved', adminNotes)}
                className="action-btn resolve"
              >
                <CheckCircle size={16} />
                Mark Resolved
              </button>
            )}
            <button 
              onClick={() => onUpdateStatus(report._id, 'rejected', adminNotes)}
              className="action-btn reject"
            >
              <AlertCircle size={16} />
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Assign Worker Modal Component
const AssignWorkerModal = ({ report, workers, onClose, onAssign }) => {
  const [selectedWorker, setSelectedWorker] = useState('');
  const [taskTitle, setTaskTitle] = useState(`Resolve ${report.issueType} issue at ${report.bin?.binId || report.binId}`);
  const [taskDescription, setTaskDescription] = useState(report.description);
  const [startTime, setStartTime] = useState(new Date().toISOString().slice(0, 16));
  const [endTime, setEndTime] = useState(new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString().slice(0, 16));

  const handleAssign = () => {
    if (!selectedWorker) {
      toast.error('Please select a worker');
      return;
    }

    onAssign(report._id, selectedWorker, {
      title: taskTitle,
      description: taskDescription,
      startTime: new Date(startTime),
      endTime: new Date(endTime)
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content assign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Assign Report to Worker</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-body">
          <div className="assign-form">
            <div className="form-group">
              <label>Select Worker</label>
              <select 
                value={selectedWorker}
                onChange={(e) => setSelectedWorker(e.target.value)}
                className="form-select"
              >
                <option value="">Choose a worker...</option>
                {workers.map(worker => (
                  <option key={worker._id} value={worker._id}>
                    {worker.name} - {worker.zone || 'No zone assigned'}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Task Title</label>
              <input
                type="text"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Task Description</label>
              <textarea
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                className="form-textarea"
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="datetime-local"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <input
                  type="datetime-local"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="form-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-btn">
            Cancel
          </button>
          <button onClick={handleAssign} className="assign-btn">
            <UserCheck size={16} />
            Assign Task
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportManagement;
